import React, { useEffect, useMemo, useState } from "react";
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer, } from "recharts";
import { Disclosure } from "@headlessui/react";
import { ChevronUp } from "lucide-react";

import { ArrowPathIcon, BanknotesIcon, FunnelIcon, ShoppingCartIcon, TicketIcon } from "@heroicons/react/20/solid";
import { TwListBox } from "../../../components/tailwind";
import CustomActiveShapePieChart from "./CustomActiveShapePieChart";
import TwProgressBar from "../../../components/tailwind/TwProgressBar";
import { commissionType } from "../../../utils/constants";
import { useTranslation } from "react-i18next";

/* ─── Demo data – replace with API result ─────────────────── */
const rawData = [/* paste the long JSON array you posted here */];

const COLORS = ["#8884d8", "#82ca9d", "#ffc658"];

/* ─── Helpers ─────────────────────────────────────────────── */
function buildTierRowsV0(item) {
     const { commissionModelType, unitType, tiers, calculateSalesCommission } = item;
     const details = calculateSalesCommission?.details ?? [];

     // Flat model: one “tier”
     if (commissionModelType === "flat") {
          const ticketsSold = calculateSalesCommission.totalTickets;
          const revenue = item.totalTicketsEarning;
          const rate =
               unitType === "flatFeePerTicket"
                    ? `€${tiers[0].commissionPerTicket}`
                    : `${tiers[0].commissionPerTicket}%`;
          const commissionAmount =
               unitType === "flatFeePerTicket"
                    ? ticketsSold * tiers[0].commissionPerTicket
                    : details.reduce((sum, d) => sum + d.ticketPrice * tiers[0].commissionPerTicket / 100, 0);

          return [
               {
                    tierLabel: "Flat",
                    ticketsSold,
                    revenue,
                    rate,
                    commissionAmount: commissionAmount,
               },
          ];
     }

     // Tiered % model
     return tiers.map((tr) => {
          const from = tr.from;
          const to = tr.to ?? Infinity;
          const rowsInTier = details.filter(
               (d) => d.ticketIndex >= from && d.ticketIndex <= to
          );

          const ticketsSold = rowsInTier.length;
          const revenue = rowsInTier.reduce((sum, d) => sum + d.ticketPrice, 0);
          const rate =
               unitType === "percentageOnTicketPrice"
                    ? `${tr.commissionPerTicket}%`
                    : `€${tr.commissionPerTicket}`;
          const commissionAmount =
               unitType === "percentageOnTicketPrice"
                    ? revenue * tr.commissionPerTicket / 100
                    : ticketsSold * tr.commissionPerTicket;

          return {
               tierLabel: tr.to ? `${tr.from}–${tr.to}` : `${tr.from}+`,
               ticketsSold,
               revenue,
               rate,
               commissionAmount,
          };
     });
}

function buildTierRows(item) {
     const {
          commissionModelType,
          unitType,
          tiers = [],
          calculateSalesCommission = {}
     } = item;

     const details = calculateSalesCommission.details ?? [];
     const ticketsSold = calculateSalesCommission.totalTickets ?? 0;
     const revenue = item.totalTicketsEarning ?? 0;

     // Retrospective logic: apply single tier to all tickets
     if (commissionModelType == "thresholdBased") {
          const applicableTier = tiers.find(t =>
               ticketsSold >= (t.from ?? 0) && ticketsSold <= (t.to ?? Infinity)
          );

          const rate = applicableTier?.commissionPerTicket ?? 0;

          const commissionAmount =
               unitType === "flatFeePerTicket"
                    ? rate * ticketsSold
                    : details.reduce((sum, d) => sum + (d.ticketPrice * rate) / 100, 0);

          const rateFormatted =
               unitType === "flatFeePerTicket" ? `€${rate}` : `${rate}%`;

          return [
               {
                    // tierLabel: `${applicableTier?.from}–${applicableTier?.to ?? '∞'} (Applied Retrospectively)`,
                    tierLabel: `greater than / equal to ${applicableTier?.from} `,
                    ticketsSold,
                    revenue,
                    rate: rateFormatted,
                    commissionAmount,
               }
          ];
     }

     // Flat model (non-retrospective): one tier
     if (commissionModelType === "flat") {
          const rate =
               unitType === "flatFeePerTicket"
                    ? `€${tiers[0].commissionPerTicket}`
                    : `${tiers[0].commissionPerTicket}%`;

          const commissionAmount =
               unitType === "flatFeePerTicket"
                    ? ticketsSold * tiers[0].commissionPerTicket
                    : details.reduce(
                         (sum, d) => sum + (d.ticketPrice * tiers[0].commissionPerTicket) / 100,
                         0
                    );

          return [
               {
                    tierLabel: "Flat",
                    ticketsSold,
                    revenue,
                    rate,
                    commissionAmount,
               }
          ];
     }

     // Tiered model (non-retrospective)
     return tiers.map((tr) => {
          const from = tr.from ?? 0;
          const to = tr.to ?? Infinity;

          const rowsInTier = details.filter(
               (d) => d.ticketIndex >= from && d.ticketIndex <= to
          );

          const ticketsSold = rowsInTier.length;
          const revenue = rowsInTier.reduce((sum, d) => sum + d.ticketPrice, 0);

          const rate =
               unitType === "percentageOnTicketPrice"
                    ? `${tr.commissionPerTicket}%`
                    : `€${tr.commissionPerTicket}`;

          const commissionAmount =
               unitType === "percentageOnTicketPrice"
                    ? (revenue * tr.commissionPerTicket) / 100
                    : ticketsSold * tr.commissionPerTicket;

          return {
               tierLabel: tr.to ? `${tr.from}–${tr.to}` : `${tr.from}+`,
               ticketsSold,
               revenue,
               rate,
               commissionAmount,
          };
     });
}

function transformData(raw) {
     return raw.map((item) => {
          const tierRows = buildTierRows(item);
          return {
               partner: item.name || item.sellerReferenceId,
               campaign: item.offerLabel || "",
               ticketsSold: item.totalTicketsBooking,
               commissionType: item.commissionModelType === "flat" ? "Flat" : "Tiered",
               commissionModelType: item.commissionModelType,
               unitType: item.unitType,
               tiers: tierRows,
               ticketsEarning: item.totalTicketsEarning,
               addonsEarning: item.totalAddonsEarning,
               bonusEarned: item.calculateSalesCommission?.bonusEarned ?? 0,
               totalCommission: item.calculateSalesCommission?.totalCommission ?? 0,
               finalCommissionWithBonusApplied: item.calculateSalesCommission?.finalCommissionWithBonusApplied ?? 0,
               calculationShortBreakdown: item.calculateSalesCommission?.commissionBreakdown?.calculationShortBreakdown ?? 0,
               ticketTypes: item.eventPricingList ?? [],
               addonItems: item.addonsPricingList ?? [],
          };
     });
}

export default function SalesDistributionDashboard(props) {
     const { t, i18n } = useTranslation();
     const { eventScheduleMetricsDetail, salesChannelsWithEventMetrics } = props
     const [metricsFilter, setMetricsFilter] = React.useState('all');
     const [metricsData, setMetricsData] = React.useState({});
     const [rawCommissionData, setRawCommissionData] = useState([]);
     const commissionData = useMemo(() => transformData(rawCommissionData), [rawCommissionData]);

     useEffect(() => {
          if (metricsFilter == "all" && metricsData && Object.keys(metricsData).length <= 0 && eventScheduleMetricsDetail.salesEventMetrics && eventScheduleMetricsDetail.salesEventMetrics.length > 0) {
               setMetricsData(eventScheduleMetricsDetail.salesEventMetrics ? eventScheduleMetricsDetail.salesEventMetrics.find((f) => f.sellerId == "" && f.name == "all") : {})

          }
          setRawCommissionData(salesChannelsWithEventMetrics.filter((f) => f.sellerId !== "direct_sales"))
     }, [salesChannelsWithEventMetrics])

     return (
          <>
               <div className="flex justify-between border-b-2 border-bright-gray p-5">
                    <h3 className={"text-black text-2xl sm:text-3xl"} >
                         {t("myEvents.salesDistribution")}
                    </h3>
                    <div className="flex gap-2 justify-end items-center">
                         {/* <TwButton variant={"default"} onClick={() => {
                                       dispatch(
                                         dashboardActions.setUpdateSessionComp(
                                           dashboardReduxState.updateSessionComp + 1
                                         )
                                       )
                                     }}>
                                       <ArrowPathIcon aria-hidden="true" className="size-5 text-black forced-colors:text-[Highlight]" />
                                     </TwButton> */}
                         {/* <TwListBox
                              value={metricsFilter}
                              options={
                                   [

                                        { value: "all", title: t("myEvents.allSalesChannels"), data: eventScheduleMetricsDetail.salesEventMetrics ? eventScheduleMetricsDetail.salesEventMetrics.find((f) => f.sellerId == "" && f.name == "all") : {} },
                                        ...eventScheduleMetricsDetail.salesEventMetrics ? eventScheduleMetricsDetail.salesEventMetrics.filter((f) => f.isDeleted == false).map((d) => ({ value: d.sellerId, title: d.name === "all" ? t("myEvents.allSalesChannels") : (d.name === "direct_sales" ? "Direct Sales" : d.name), data: d })) : []
                                   ]}
                              dropdownArrow={(<FunnelIcon aria-hidden="true" className="size-5 text-black forced-colors:text-[Highlight]" />)}
                              onChange={(v) => {
                                   setMetricsFilter(v.value)
                                   setMetricsData(v.data)
                              }}
                         /> */}
                    </div>
               </div>
               <div className="flex flex-col gap-3 p-5">

                    {(<div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                         {/* Tickets Pie Chart */}
                         <div className="bg-white rounded-lg ring-1 ring-inset ring-gray-300 p-4">
                              <h3 className="text-lg font-semibold mb-4 text-center">{t("myEvents.ticketsDistribution")}</h3>
                              {/* <ResponsiveContainer width="100%" height={250}>
                                              <PieChart>
                                                <Pie
                                                  // data={timelineData.map(d => ({ name: d.date, value: d.tickets }))}
                                                  data={salesChannelsWithEventMetrics.length > 0 ? salesChannelsWithEventMetrics.map((d) => ({ value: d.totalTicketsBooking, name: d.name })) : []}
                                                  dataKey="value"
                                                  nameKey="name"
                                                  cx="50%"
                                                  cy="50%"
                                                  outerRadius={80}
                                                  label
                                                >
                                                  {timelineData.map((_, index) => (
                                                    <Cell key={`cell-t-${index}`} fill={COLORS[index % COLORS.length]} />
                                                  ))}
                                                </Pie>
                                                <Tooltip />
                                              </PieChart>
                                            </ResponsiveContainer> */}
                              <CustomActiveShapePieChart
                                   chartTitle={t("myEvents.tickets")}
                                   data={salesChannelsWithEventMetrics.length > 0 ? salesChannelsWithEventMetrics.map((d) => ({ value: d.totalTicketsBooking, name: d.name })) : []}
                              />
                         </div>

                         {/* Add-ons Pie Chart */}
                         <div className="bg-white rounded-lg  ring-1 ring-inset ring-gray-300 p-4">
                              <h3 className="text-lg font-semibold mb-4 text-center">{t("myEvents.addonsDistribution")}</h3>
                              <CustomActiveShapePieChart
                                   chartTitle={t("myEvents.addOns")}
                                   data={salesChannelsWithEventMetrics.length > 0 ? salesChannelsWithEventMetrics.map((d) => ({ value: d.totalAddonsBooking, name: d.name })) : []}
                              />
                         </div>

                         {/* Revenue Pie Chart */}
                         {false && (<div className="bg-white rounded-lg  ring-1 ring-inset ring-gray-300 p-4">
                              <h3 className="text-lg font-semibold mb-4 text-center">{t("myEvents.revenueDistribution")}</h3>
                              <CustomActiveShapePieChart
                                   chartTitle={t("myEvents.revenue")}
                                   unitSign="€"
                                   data={salesChannelsWithEventMetrics.length > 0 ? salesChannelsWithEventMetrics.map((d) => ({ value: d.totalEarning, name: d.name })) : []}
                              />
                         </div>)}
                    </div>)}
                    {false && "removed as of now" === "" && (<div className="grid grid-cols-1 md:grid-cols-2 gap-6 justify-center items-center">
                         {/* Commission Pie Chart */}
                         <div className="bg-white rounded-lg  ring-0 ring-inset ring-gray-300 p-4">
                              {/* <h3 className="text-lg font-semibold mb-4 text-center">Commission Distribution</h3> */}
                              <CustomActiveShapePieChart
                                   chartTitle={t("myEvents.commission")}
                                   unitSign="€"
                                   data={salesChannelsWithEventMetrics.length > 0 && salesChannelsWithEventMetrics.filter((f) => f.sellerId !== "direct_sales").length > 0 ? salesChannelsWithEventMetrics.filter((f) => f.sellerId !== "direct_sales").map((d) => ({ value: d.calculateSalesCommission.finalCommissionWithBonusApplied, name: d.name })) : []}
                              />
                              {/* {salesChannelsWithEventMetrics.length} */}
                              {salesChannelsWithEventMetrics && salesChannelsWithEventMetrics.filter((f) => f.sellerId !== "direct_sales").length <= 0 && (<span className="absolute left-0 top-0 bottom-0 right-0 bg-gray-200 flex justify-center items-center rounded-lg">There is no booking yet.</span>)}
                         </div>
                         <div>
                              {metricsData?.eventPricingList && metricsData?.eventPricingList.map((item, i) => {
                                   const percent = parseFloat(((item.ticketsBookedConfirmed ? item.ticketsBookedConfirmed : 0) / item.capacity) * 100).toFixed(2);
                                   return (
                                        <div key={i} className="flex flex-col [items-center] w-full">
                                             <div className="text-sm text-gray-800 flex gap-1">
                                                  <span className="line-clamp-1 font-bold">{item.ticketType}</span> <span className="text-gray-400">({percent}%)</span> <span className="text-gray-400">€{item.amount}</span>
                                             </div>
                                             <div className="flex justify-between items-center gap-1">
                                                  <TwProgressBar value={percent} color={item?.color || "bg-primary"} max={100} />
                                                  <div className="text-sm font-semibold text-gray-900 w-12 text-right">{item.ticketsBookedConfirmed ? item.ticketsBookedConfirmed : 0}/{item.capacity}</div>
                                             </div>
                                        </div>
                                   )
                              })}
                         </div>
                    </div>)}

                    {false && (<div className="flex gap-3">
                         <div
                              className={
                                   "bg-white rounded-xl flex flex-col gap-4 cursor-pointer border-0 border-gray-200"
                              }
                         >
                              <dl className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-3 lg:grid-cols-3 xl:grid-cols-5">
                                   <div
                                        className="relative overflow-hidden rounded-lg bg-white border border-bright-gray px-6 pb-4 pt-5"
                                   >
                                        <dt>
                                             <div className="absolute rounded-full bg-indigo-500 p-2.5">
                                                  <ShoppingCartIcon aria-hidden="true" className="size-5 text-white" />
                                             </div>
                                             <p className="ml-12 truncate text-xs font-medium text-gray-500">{t("bookings.totalBookingTitle")}</p>
                                        </dt>
                                        <dd className="ml-12 flex items-baseline ">
                                             {/* <p className="text-xl font-semibold text-gray-900">{metricsData?.totalBooking ? metricsData.totalBooking : bookingDetails.bookedPeople.length}</p> */}
                                             <p className="text-xl font-semibold text-gray-900">{metricsData?.totalBooking ? metricsData.totalBooking : 0}</p>
                                        </dd>
                                   </div>
                                   <div
                                        className="relative overflow-hidden rounded-lg bg-white border border-bright-gray px-6 pb-4 pt-5"
                                   >
                                        <dt>
                                             <div className="absolute rounded-full bg-indigo-500 p-2.5">
                                                  <TicketIcon aria-hidden="true" className="size-5 text-white" />
                                             </div>
                                             <p className="ml-12 truncate text-xs font-medium text-gray-500">{t("myEvents.totalTicketsSold")}</p>
                                        </dt>
                                        <dd className="ml-12 flex items-baseline ">
                                             {/* <p className="text-xl font-semibold text-gray-900">{metricsData?.eventPricingList ? metricsData.eventPricingList.reduce((accumulator, item) => {
                                               return accumulator + item.ticketsBookedConfirmed;
                                             }, 0) : 0}</p> */}
                                             <p className="text-xl font-semibold text-gray-900">{metricsData?.eventPricingList ? metricsData.eventPricingList.reduce((accumulator, item) => {
                                                  return accumulator + item.ticketsBookedConfirmed;
                                             }, 0) : 0}</p>
                                             {/* }, 0) : "bookingDetails.seatsBooked"}</p> */}
                                        </dd>
                                   </div>
                                   <div
                                        className="relative overflow-hidden rounded-lg bg-white border border-bright-gray px-6 pb-4 pt-5"
                                   >
                                        <dt>
                                             <div className="absolute rounded-full bg-indigo-500 p-2.5">
                                                  <TicketIcon aria-hidden="true" className="size-5 text-white" />
                                             </div>
                                             <p className="ml-12 truncate text-xs font-medium text-gray-500">{t("myEvents.totalAddonsSold")}</p>
                                        </dt>
                                        <dd className="ml-12 flex items-baseline ">
                                             <p className="text-xl font-semibold text-gray-900">{metricsData?.addonsPricingList ? metricsData.addonsPricingList.reduce((accumulator, item) => {
                                                  return accumulator + item.addonsBookedConfirmed;
                                             }, 0) : 0}</p>
                                             {/* }, 0) : "bookingDetails.seatsBooked"}</p> */}
                                        </dd>
                                   </div>
                                   {metricsFilter == "all" || metricsFilter == "direct_sales" ? (<div
                                        className="relative overflow-hidden rounded-lg bg-white border border-bright-gray px-6 pb-4 pt-5"
                                   >
                                        <dt>
                                             <div className="absolute rounded-full bg-indigo-500 p-2.5">
                                                  <BanknotesIcon aria-hidden="true" className="size-5 text-white" />
                                             </div>
                                             <p className="ml-12 truncate text-xs font-medium text-gray-500">{t("myEvents.revenue")}</p>
                                        </dt>
                                        <dd className="ml-12 flex items-baseline ">
                                             {/* <p className="text-xl font-semibold text-gray-900">€{metricsData?.totalEarning ? metricsData.totalEarning : (bookingDetails.seatsEarning)}</p> */}
                                             <p className="text-xl font-semibold text-gray-900">€{metricsData?.totalEarning ? metricsData.totalEarning : 0.00}</p>
                                        </dd>
                                   </div>) : (
                                        <>
                                             <div
                                                  className="relative overflow-hidden rounded-lg bg-white border border-bright-gray px-6 pb-4 pt-5"
                                             >
                                                  <dt>
                                                       <div className="absolute rounded-full bg-indigo-500 p-2.5">
                                                            <BanknotesIcon aria-hidden="true" className="size-5 text-white" />
                                                       </div>
                                                       <p className="ml-12 truncate text-xs font-medium text-gray-500">{t("myEvents.ticketsRevenue")}</p>
                                                  </dt>
                                                  <dd className="ml-12 flex items-baseline ">
                                                       <p className="text-xl font-semibold text-gray-900">€{metricsData?.totalTicketsEarning ? parseFloat(metricsData.totalTicketsEarning).toFixed(2) : 0}</p>
                                                  </dd>
                                             </div>
                                             <div
                                                  className="relative overflow-hidden rounded-lg bg-white border border-bright-gray px-6 pb-4 pt-5"
                                             >
                                                  <dt>
                                                       <div className="absolute rounded-full bg-indigo-500 p-2.5">
                                                            <BanknotesIcon aria-hidden="true" className="size-5 text-white" />
                                                       </div>
                                                       <p className="ml-12 truncate text-xs font-medium text-gray-500">{t("myEvents.addonsRevenue")}</p>
                                                  </dt>
                                                  <dd className="ml-12 flex items-baseline ">
                                                       <p className="text-xl font-semibold text-gray-900">€{metricsData?.totalAddonsEarning ? parseFloat(metricsData.totalAddonsEarning).toFixed(2) : 0}</p>
                                                  </dd>
                                             </div>
                                        </>
                                   )}
                                   {metricsFilter !== "all" && metricsData?.isCommissionApplicable && metricsData?.isCommissionApplicable == true && (<div
                                        className="relative overflow-hidden rounded-lg bg-gray-200 border border-bright-gray px-6 pb-4 pt-5"
                                   >
                                        <dt>
                                             <div className="absolute rounded-full bg-indigo-500 p-2.5">
                                                  <BanknotesIcon aria-hidden="true" className="size-5 text-white" />
                                             </div>
                                             <p className="ml-12 truncate text-xs font-medium text-gray-500">{t("myEvents.commission")}</p>
                                        </dt>
                                        <dd className="ml-12 flex items-baseline ">
                                             <p className="text-xl font-semibold text-gray-900">€{metricsData?.isCommissionApplicable && metricsData?.isCommissionApplicable == true ? parseFloat(metricsData.calculateSalesCommission.finalCommissionWithBonusApplied).toFixed(2) : 0}</p>
                                        </dd>
                                   </div>)}
                              </dl>
                         </div>
                    </div>)}

               </div>

               {/* Commission breakdown removed here — commission now lives in My Promotions
                   (single source of truth via the shared commission engine + payouts). This
                   view keeps the event-level sales-distribution only. */}
               {false && (<div className="flex flex-col gap-3 p-5">
                    <h2 className="text-xl font-semibold mb-4">{t("myEvents.commissionBreakdown")}</h2>
                    <div className="space-y-4">
                         {/* {commissionData.map((data, index) => (
                              <Disclosure key={index}>
                                   {({ open }) => (
                                        <>
                                             <Disclosure.Button className="w-full flex items-center justify-between rounded-2xl border border-gray-200 bg-white px-6 py-5 text-left transition hover:shadow-sm focus:outline-none">
                                                  <div>
                                                       <h3 className="text-lg font-semibold text-gray-900">
                                                            {data.partner}
                                                       </h3>
                                                       {data.campaign && (
                                                            <p className="text-xs text-gray-400 font-normal">{data.campaign}</p>
                                                       )}
                                                       <div className="mt-1 flex items-center space-x-2 text-sm text-gray-500">
                                                            <span>{data.ticketsSold} tickets</span>
                                                            <span>·</span>
                                                            <span
                                                                 className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-medium ${data.commissionType === "Tiered"
                                                                      ? "bg-purple-100 text-purple-700"
                                                                      : "bg-green-100 text-green-700"
                                                                      }`}
                                                            >
                                                                 {data.commissionType}
                                                            </span>
                                                       </div>
                                                  </div>
                                                  <ChevronUp
                                                       className={`h-5 w-5 text-gray-700 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
                                                  />
                                             </Disclosure.Button>

                                             <Disclosure.Panel className="mx-2 mt-2 rounded-xl border border-gray-100 bg-gray-50 p-4 text-sm text-gray-700">
                                                  <div className="mb-3 flex gap-4 flex-wrap">
                                                       <div className="flex-1">
                                                            <strong>{t("myEvents.tickets")}:</strong>
                                                            <ul className="list-disc pl-5 text-xs text-gray-600">
                                                                 {data.ticketTypes.map((t, i) => (
                                                                      <li key={i}>
                                                                           {t.ticketType} — {t.ticketsBookedConfirmed} × €
                                                                           {t.amount}
                                                                      </li>
                                                                 ))}
                                                            </ul>
                                                       </div>
                                                       <div className="flex-1">
                                                            <strong>{t("myEvents.addOns")}:</strong>
                                                            <ul className="list-disc pl-5 text-xs text-gray-600">
                                                                 {data.addonItems
                                                                      .filter((a) => a.addonsBookedConfirmed > 0)
                                                                      .map((a, i) => (
                                                                           <li key={i}>
                                                                                {a.addonName} — {a.addonsBookedConfirmed} × €
                                                                                {a.amountForTicket}
                                                                           </li>
                                                                      ))}
                                                            </ul>
                                                       </div>
                                                  </div>
                                                  <p className="mb-2">
                                                       <strong>Model:</strong> {data.commissionModel}
                                                  </p>
                                                  <p className="mb-2">
                                                       <strong>Calculation:</strong> {data.calculationBreakdown}
                                                  </p>
                                                  <div className="flex flex-wrap gap-2 mt-2">
                                                       <span className="inline-block rounded-full bg-blue-100 px-2 py-1 text-xs text-blue-800">
                                                            {t("myEvents.tickets")} €{data.ticketsEarning.toFixed(2)}
                                                       </span>
                                                       <span className="inline-block rounded-full bg-amber-100 px-2 py-1 text-xs text-amber-800">
                                                            {t("myEvents.addOns")} €{data.addonsEarning.toFixed(2)}
                                                       </span>
                                                       <span className="inline-block rounded-full bg-green-100 px-2 py-1 text-xs text-green-800">
                                                            {t("myEvents.commission")} €{data.totalCommission.toFixed(2)}
                                                       </span>
                                                  </div>
                                             </Disclosure.Panel>
                                        </>
                                   )}
                              </Disclosure>
                         ))} */}


                         {commissionData.map((p, i) => (
                              <Disclosure key={i}>
                                   {({ open }) => (
                                        <>
                                             <Disclosure.Button className="flex w-full items-center justify-between rounded-xl border bg-white px-6 py-4 text-left shadow-sm">
                                                  <div>
                                                       <h3 className="text-lg font-semibold">{p.partner}</h3>
                                                       {p.campaign && (
                                                            <p className="text-xs text-gray-400 font-normal">{p.campaign}</p>
                                                       )}
                                                       <p className="flex items-center space-x-2 text-sm text-gray-500">
                                                            {/* <span>{p.ticketsSold} tickets</span> */}
                                                            <span className="inline-block rounded-full bg-blue-100 px-2 py-1 text-xs text-blue-800">
                                                                 {t("myschedule.ticketsSold")}: {p.ticketsSold}
                                                            </span>
                                                            {/* <span>·</span>
                                                            <span
                                                                 className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-medium ${p.commissionType === "Tiered"
                                                                      ? "bg-purple-100 text-purple-700"
                                                                      : "bg-green-100 text-green-700"
                                                                      }`}
                                                            >
                                                                 {p.commissionType}
                                                            </span> */}
                                                            <span>·</span>
                                                            <span className="inline-block rounded-full bg-green-100 px-2 py-1 text-xs text-green-800">{t("myEvents.commission")}: €{p.finalCommissionWithBonusApplied.toFixed(2)}</span>
                                                       </p>
                                                  </div>
                                                  <ChevronUp
                                                       className={`h-5 w-5 transition-transform ${open ? "rotate-180" : ""
                                                            }`}
                                                  />
                                             </Disclosure.Button>

                                             <Disclosure.Panel className="mt-2 rounded-xl border bg-gray-50 p-4">
                                                  <div className="overflow-x-auto">
                                                       <table className="min-w-full text-xs text-left border">
                                                            <thead className="bg-gray-100">
                                                                 <tr>
                                                                      <th className="px-4 py-2 border ">{p.commissionModelType == "thresholdBased" ? t("myEvents.appliedThreshold") : t("myEvents.tier")}</th>
                                                                      <th className="px-4 py-2 border max-w-60 w-60">{t("myEvents.rate")} ({commissionType[p.unitType]})</th>
                                                                      <th className="px-4 py-2 border ">{t("myEvents.ticketsSold")}</th>
                                                                      <th className="px-4 py-2 border ">{t("myEvents.revenue")}</th>
                                                                      <th className="px-4 py-2 border max-w-72 w-72">{t("myEvents.commission")}</th>
                                                                 </tr>
                                                            </thead>
                                                            <tbody>
                                                                 {p.tiers.map((tier, idx) => (
                                                                      <tr key={idx} className="border-t">
                                                                           <td className="px-4 py-2 border">{tier.tierLabel}</td>
                                                                           <td className="px-4 py-2 border">{tier.rate}</td>
                                                                           <td className="px-4 py-2 border">{tier.ticketsSold}</td>
                                                                           <td className="px-4 py-2 border">
                                                                                €{tier.revenue.toFixed(2)}
                                                                           </td>
                                                                           <td className="px-4 py-2 border">
                                                                                €{tier.commissionAmount.toFixed(2)}
                                                                                <span className="text-gray-500">
                                                                                     {` (${tier.rate} ${p.unitType === "percentageOnTicketPrice" ? `${t("myEvents.of")} €${tier.revenue.toFixed(2)} ${t("myEvents.revenue")}` : `x ${tier.ticketsSold} ${t("myEvents.ticket")}`})`}
                                                                                     {/* {`(${p?.calculationShortBreakdown || ""})`} */}
                                                                                </span>
                                                                           </td>
                                                                      </tr>
                                                                 ))}
                                                                 <tr className="bg-gray-50 font-semibold">
                                                                      <td colSpan={3} className="px-4 py-2 border">
                                                                           {t("myEvents.total")}
                                                                      </td>
                                                                      <td className="px-4 py-2 border">
                                                                           €{p.ticketsEarning.toFixed(2)}
                                                                      </td>
                                                                      <td className="px-4 py-2 border">
                                                                           €{p.totalCommission.toFixed(2)}
                                                                      </td>
                                                                 </tr>
                                                                 {/* Summary rows */}
                                                                 <tr className="bg-gray-50 font-semibold">
                                                                      <td colSpan={4} className="px-4 py-2 border">
                                                                           {t("myEvents.bonusEarned")}
                                                                      </td>
                                                                      <td className="px-4 py-2 border">
                                                                           €{p.bonusEarned.toFixed(2)}
                                                                      </td>
                                                                 </tr>
                                                                 <tr className="bg-gray-100 font-bold">
                                                                      <td colSpan={4} className="px-4 py-2 border">
                                                                           {t("myEvents.totalPayout")}
                                                                      </td>
                                                                      <td className="px-4 py-2 border">
                                                                           €{(p.finalCommissionWithBonusApplied).toFixed(2)}
                                                                      </td>
                                                                 </tr>
                                                            </tbody>
                                                       </table>
                                                  </div>
                                             </Disclosure.Panel>
                                        </>
                                   )}
                              </Disclosure>
                         ))}
                    </div>

               </div>)}
          </>
     );
}
